/**
 * Spaced Repetition System (SRS) Engine
 * 
 * Implements the SM-2 algorithm for optimal review scheduling.
 * Based on the SuperMemo-2 algorithm by Piotr Wozniak.
 * 
 * Key features:
 * - Adaptive intervals based on performance
 * - Ease factor adjustments
 * - Four rating levels: again, hard, good, easy
 */

import type { SRSCard, SRSRating, AnsweredQuestion } from '../types/question';

/** Default ease factor for new cards */
const DEFAULT_EASE_FACTOR = 2.5;

/** Minimum ease factor to prevent excessive intervals */
const MIN_EASE_FACTOR = 1.3;

/**
 * Creates a new SRS card for a question
 */
export function createSRSCard(questionId: string): SRSCard {
  return {
    questionId,
    interval: 0,
    repetitions: 0,
    easeFactor: DEFAULT_EASE_FACTOR,
    nextReview: new Date(),
  };
}

/**
 * Calculate next review date based on rating
 * Implements SM-2 algorithm
 */
export function calculateNextReview(
  card: SRSCard,
  rating: SRSRating
): SRSCard {
  let { interval, repetitions, easeFactor } = card;

  switch (rating) {
    case 'again':
      // Failed: reset repetitions, keep interval at 1 day
      repetitions = 0;
      interval = 1;
      easeFactor = Math.max(MIN_EASE_FACTOR, easeFactor - 0.2);
      break;

    case 'hard':
      // Hard: increase repetitions slightly, small interval increase
      repetitions += 1;
      interval = interval * 1.2;
      easeFactor = Math.max(MIN_EASE_FACTOR, easeFactor - 0.15);
      break;

    case 'good':
      // Good: standard SM-2 progression
      repetitions += 1;
      if (repetitions === 1) {
        interval = 1;
      } else if (repetitions === 2) {
        interval = 6;
      } else {
        interval = Math.round(interval * easeFactor);
      }
      break;

    case 'easy':
      // Easy: increase ease factor, larger interval
      repetitions += 1;
      easeFactor += 0.15;
      if (repetitions === 1) {
        interval = 4;
      } else {
        interval = Math.round(interval * easeFactor * 1.3);
      }
      break;
  }

  // Calculate next review date
  const nextReview = new Date();
  nextReview.setDate(nextReview.getDate() + Math.round(interval));

  return {
    ...card,
    interval,
    repetitions,
    easeFactor,
    nextReview,
    lastRating: rating,
  };
}

/**
 * Get human-readable interval description
 */
export function getIntervalDescription(days: number): string {
  if (days === 0) return 'Now';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days === 7) return '1 week';
  if (days < 30) return `${Math.round(days / 7)} weeks`;
  if (days < 365) return `${Math.round(days / 30)} months`;
  return `${Math.round(days / 365)} years`;
}

/**
 * Get next review date description
 */
export function getNextReviewDate(card: SRSCard): string {
  return getIntervalDescription(card.interval);
}

/**
 * Check if a card is due for review
 */
export function isDueForReview(card: SRSCard): boolean {
  const now = new Date();
  return card.nextReview <= now;
}

/**
 * Get cards that are due for review
 */
export function getDueCards(cards: SRSCard[]): SRSCard[] {
  return cards.filter(isDueForReview);
}

/**
 * Sort cards by next review date (earliest first)
 */
export function sortByNextReview(cards: SRSCard[]): SRSCard[] {
  return [...cards].sort((a, b) => a.nextReview.getTime() - b.nextReview.getTime());
}

/**
 * Get SRS stats for display
 */
export function getSRSStats(cards: SRSCard[]): {
  total: number;
  due: number;
  new: number;
  learning: number;
  mastered: number;
} {
  const now = new Date();
  const due = cards.filter(c => c.nextReview <= now).length;
  const newCards = cards.filter(c => c.repetitions === 0).length;
  const learning = cards.filter(c => c.repetitions > 0 && c.repetitions < 3).length;
  const mastered = cards.filter(c => c.repetitions >= 3).length;

  return {
    total: cards.length,
    due,
    new: newCards,
    learning,
    mastered,
  };
}

/**
 * Initialize SRS cards from answered questions
 */
export function initializeFromAnswers(
  answers: AnsweredQuestion[]
): SRSCard[] {
  const cardMap = new Map<string, SRSCard>();

  for (const answer of answers) {
    const existing = cardMap.get(answer.id);

    if (!existing) {
      // Create new card
      const card = createSRSCard(answer.id);
      const rating = answer.wasCorrect ? 'good' : 'again';
      const updated = calculateNextReview(card, rating);
      cardMap.set(answer.id, updated);
    } else {
      // Update existing card based on new answer
      const rating = answer.wasCorrect ? 'good' : 'again';
      const updated = calculateNextReview(existing, rating);
      cardMap.set(answer.id, updated);
    }
  }

  return Array.from(cardMap.values());
}

/**
 * Get interval preview for each rating option
 * Shows what the interval would be if that rating was selected
 */
export function getRatingPreviews(card: SRSCard): Record<SRSRating, string> {
  const ratings: SRSRating[] = ['again', 'hard', 'good', 'easy'];
  const previews: Partial<Record<SRSRating, string>> = {};

  for (const rating of ratings) {
    const nextCard = calculateNextReview(card, rating);
    previews[rating] = getIntervalDescription(nextCard.interval);
  }

  return previews as Record<SRSRating, string>;
}

/**
 * Export cards to JSON for persistence
 */
export function exportCards(cards: SRSCard[]): string {
  // JSON.stringify already coerces Date via Date.prototype.toJSON → ISO string.
  // Guard against the case where the value is already a string (post-toJSON).
  return JSON.stringify(cards, (key, value) => {
    if (key === 'nextReview' && value instanceof Date) {
      return value.toISOString();
    }
    return value;
  });
}

/**
 * Import cards from JSON
 */
export function importCards(json: string): SRSCard[] {
  return JSON.parse(json, (key, value) => {
    if (key === 'nextReview') {
      return new Date(value);
    }
    return value;
  });
}

/**
 * Merge SRS cards (for syncing across sessions)
 * Keeps the card with more repetitions (more learning history)
 */
export function mergeCards(
  local: SRSCard[],
  remote: SRSCard[]
): SRSCard[] {
  const merged = new Map<string, SRSCard>();

  // Add all local cards
  for (const card of local) {
    merged.set(card.questionId, card);
  }

  // Merge remote cards
  for (const card of remote) {
    const existing = merged.get(card.questionId);
    if (!existing || card.repetitions > existing.repetitions) {
      merged.set(card.questionId, card);
    }
  }

  return Array.from(merged.values());
}
